import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Text,
    Icon,
    Input,
    VStack,
    HStack,
    Button,
    useToast,
} from '@chakra-ui/react';
import { FaFileUpload, FaTrashAlt } from 'react-icons/fa';

interface FileDropZoneProps {
    onFileSelect: (file: File | null) => void;
    selectedFile: File | null;
}

const FileDropZone: React.FC<FileDropZoneProps> = ({ onFileSelect, selectedFile: propSelectedFile }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(propSelectedFile);
    const [dragOver, setDragOver] = useState(false);
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        setSelectedFile(propSelectedFile);
    }, [propSelectedFile]);

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setDragOver(false);

        const file = event.dataTransfer.files[0];
        if (file && (file.type === 'application/pdf' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
            setSelectedFile(file);
            onFileSelect(file);
        } else {
            toast({
                title: 'Invalid file type',
                description: 'Only PDF and DOCX files are allowed.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && (file.type === 'application/pdf' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
            setSelectedFile(file);
            onFileSelect(file);
        } else {
            toast({
                title: 'Invalid file type',
                description: 'Only PDF and DOCX files are allowed.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleClearSelection = () => {
        setSelectedFile(null);
        onFileSelect(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <Box>
            <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                borderWidth="2px"
                borderRadius="lg"
                borderStyle="dashed"
                borderColor={dragOver ? 'brand.900' : 'gray.300'}
                bg={dragOver ? 'brand.600' : 'white'}
                w="full"
                p="6"
                textAlign="center"
                cursor="pointer"
                position="relative"
            >
                <VStack spacing="4">
                    <Icon as={FaFileUpload} w={10} h={10} color="brand.900" />
                    <Text fontSize="lg" fontWeight="bold" color="gray.600">
                        Drag and Drop file here or{' '}
                        <Text as="span" color="brand.900" textDecoration="underline">
                            Choose file
                        </Text>
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                        Supported file formats: <strong>.pdf, .docx</strong>, up to 50 MB
                    </Text>
                </VStack>
                <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx"
                    onChange={handleFileInput}
                    position="absolute"
                    top="0"
                    left="0"
                    width="100%"
                    height="100%"
                    opacity="0"
                    cursor="pointer"
                />
            </Box>
            {selectedFile && (
                <HStack mt="2" justifyContent="space-between">
                    <Text fontSize="sm" color="gray.700">
                        {selectedFile.name}
                    </Text>
                    <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={handleClearSelection}
                        leftIcon={<Icon as={FaTrashAlt} />}
                    >
                        Clear
                    </Button>
                </HStack>
            )}
        </Box>
    );
};

export default FileDropZone;
